/**
 * Image tool results — one definition of "this result is a picture, not text".
 *
 * `screenshot` returns a `data:image/png;base64,…` URL (the product's own
 * contract, which `mcp-server-live.ts` turns into an MCP `image` block, and the
 * hosted session sends as one: hosted-tools.ts). The text truncation other
 * results get would slice a PNG data URL into garbage, so an image is recognised
 * before anything else is done with a result.
 */

/** What the gateway cuts a text result to (ml-services src/agent/history.ts); a tool that writes long results budgets for it. */
export const TOOL_RESULT_MAX_CHARS = 8000;

/** A whole result that is nothing but an image data URL. */
export const IMAGE_DATA_URL_RE = /^data:(image\/[a-zA-Z+.-]+);base64,([A-Za-z0-9+/=]+)$/;

export interface ToolImage {
    /** The original `data:…` URL, unmodified. */
    dataUrl: string;
    mime: string;
    /** The payload after the comma — what an MCP image block carries. */
    base64: string;
    /** Rough decoded size for logs — base64 is ~4/3 of the bytes. */
    bytes: number;
    /** Pixel size, when the header says (PNG only). */
    width?: number;
    height?: number;
}

/**
 * Width/height out of a PNG's IHDR chunk, which sits at a fixed offset right
 * after the 8-byte signature. 32 base64 chars decode to the 24 bytes that
 * covers. Anything else (JPEG, WebP, a stub) gives null and the caller says
 * less in its text block.
 */
export function pngDimensions(base64: string): { width: number; height: number } | null {
    if (base64.length < 32) return null;
    let bin: string;
    try {
        bin = atob(base64.slice(0, 32));
    } catch {
        return null;
    }
    if (bin.length < 24 || bin.slice(1, 4) !== 'PNG' || bin.slice(12, 16) !== 'IHDR') return null;
    const be32 = (o: number) => (
        (bin.charCodeAt(o) << 24) | (bin.charCodeAt(o + 1) << 16)
        | (bin.charCodeAt(o + 2) << 8) | bin.charCodeAt(o + 3)
    ) >>> 0;
    const width = be32(16);
    const height = be32(20);
    return width > 0 && height > 0 ? { width, height } : null;
}

/** An image tool result, or null for ordinary text. Never throws. */
export function readToolImage(result: string): ToolImage | null {
    const trimmed = result.trim();
    const m = IMAGE_DATA_URL_RE.exec(trimmed);
    if (!m) return null;
    const base64 = m[2];
    const image: ToolImage = {
        dataUrl: trimmed,
        mime: m[1],
        base64,
        bytes: Math.floor(base64.length * 0.75),
    };
    const size = pngDimensions(base64);
    if (size) {
        image.width = size.width;
        image.height = size.height;
    }
    return image;
}

/** The one-line description that rides with the image. */
export function describeToolImage(toolName: string, image: ToolImage): string {
    return image.width && image.height
        ? `${toolName} captured ${image.width}x${image.height}`
        : `${toolName} captured an ${image.mime} image`;
}
